import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { logFoodAPI } from '../../../utils/api';
import RecentlyLoggedList from '../../../components/logfood/RecentlyLoggedList';
import FoodResultRow from '../../../components/logfood/FoodResultRow';
import FallbackChips from '../../../components/logfood/FallbackChips';
import { searchItemToFood } from '../../../utils/logFoodMappers';

const PAGE_SIZE = 10;

/**
 * Meals tab — Mongo-backed (always live).
 *
 * Landing: Recently logged (scope=meals) + saved meals + curated meals.
 * With query: curated + saved sections filtered by name match.
 * "Build a new meal" routes to the existing /add-custom-meal page.
 */
export default function MealsTab({
  recents,
  recentsLoading,
  onSelectFood,
  onSelectRecent,
  onQuickLog,
  onQuickLogRecent,
  onTryTab,
  onCreateManual,
  onSearchAllRecents,
  date,
  mealType,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [curated, setCurated] = useState([]);
  const [saved, setSaved] = useState([]);
  const [matchingRecents, setMatchingRecents] = useState([]);
  const [total, setTotal] = useState(0);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const doFetch = useCallback(function (q) {
    setLoading(true);
    const params = { limit: PAGE_SIZE * 2 };
    if (q && q.trim()) params.q = q.trim();
    const recentsPromise =
      q && q.trim()
        ? logFoodAPI.getRecentFoods({ scope: 'meals', q: q.trim(), limit: 3 })
            .catch(function () { return { data: [] }; })
        : Promise.resolve({ data: [] });

    Promise.all([
      logFoodAPI.searchMeals(params).catch(function () { return { data: { curated: [], saved: [], total: 0 } }; }),
      recentsPromise,
    ]).then(function (arr) {
      const data = (arr[0] && arr[0].data) || {};
      setCurated(Array.isArray(data.curated) ? data.curated : []);
      setSaved(Array.isArray(data.saved) ? data.saved : []);
      setTotal(Number(data.total || 0));
      setFallback(data.fallback_suggestion || null);
      setMatchingRecents(Array.isArray(arr[1].data) ? arr[1].data.slice(0, 3) : []);
      setLoading(false);
    });
  }, []);

  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function () { doFetch(query); }, 300);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doFetch]);

  const handleBuildNewMeal = function () {
    const params = new URLSearchParams({
      date: date || '',
      type: mealType || 'Lunch',
      return: '/log-food',
      tab: 'meals',
    });
    navigate('/add-meal?' + params.toString());
  };

  const isLanding = !query.trim();

  return (
    <div className="flex flex-col" data-testid="meals-tab">
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            placeholder="Search meals..."
            className="w-full pl-9 pr-9 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="meals-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={function () { setQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 size-5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
              aria-label="Clear search"
              data-testid="meals-search-clear"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {isLanding && (
        <RecentlyLoggedList
          items={recents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          emptyMessage={recentsLoading ? 'Loading…' : null}
        />
      )}

      {!isLanding && matchingRecents.length > 0 && !loading && (
        <RecentlyLoggedList
          items={matchingRecents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          trailingAction={
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={function () { setQuery(''); }}
              data-testid="meals-search-all-recents"
            >
              Show all recents →
            </button>
          }
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && saved.length > 0 && (
        <>
          <div className="px-4 py-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Your Saved Meals
            </h3>
          </div>
          <ul className="divide-y divide-border">
            {saved.map(function (item) {
              return (
                <FoodResultRow
                  key={item.id || item.external_id}
                  item={item}
                  onSelect={function () { onSelectFood(searchItemToFood(item)); }}
                  onQuickLog={function () { onQuickLog(searchItemToFood(item)); }}
                />
              );
            })}
          </ul>
        </>
      )}

      {!loading && curated.length > 0 && (
        <>
          <div className="px-4 py-2 mt-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Fitxess Curated
            </h3>
          </div>
          <ul className="divide-y divide-border">
            {curated.map(function (item) {
              return (
                <FoodResultRow
                  key={item.id || item.external_id}
                  item={item}
                  onSelect={function () { onSelectFood(searchItemToFood(item)); }}
                  onQuickLog={function () { onQuickLog(searchItemToFood(item)); }}
                />
              );
            })}
          </ul>
        </>
      )}

      {!loading && total === 0 && !isLanding && fallback && (
        <FallbackChips
          suggestion={fallback}
          query={query}
          onTryTab={onTryTab}
          onCreateManual={onCreateManual}
        />
      )}

      {/* Build a new meal CTA — always visible at the bottom of the tab */}
      <div className="px-4 py-4 mt-2 border-t border-border">
        <Button
          variant="default"
          className="w-full gap-2"
          onClick={handleBuildNewMeal}
          data-testid="meals-build-new-btn"
        >
          <Plus className="size-4" />
          Build a new meal
        </Button>
      </div>
    </div>
  );
}
